/**
 * PostHog, loaded off the critical path (browser only).
 *
 * posthog-js is reachable ONLY through the dynamic import in loadPosthog(): it never enters a page's
 * initial bundle. PostHogLoader imports this module after window `load`, inside requestIdleCallback
 * (2 s timeout; a 2 s timer where rIC is missing), and calls startPosthog(). Everything here is fire-and-forget: no function throws, returns a promise or
 * waits on PostHog, so a capture can never block or break the setup / payment flow. Captures made
 * before the library is ready are queued (capped) and flushed once it is.
 *
 * Identity: the first-party `_lmid` cookie is the anonymous id. It is passed to PostHog as
 * bootstrap.distinctID, so the id sent with setup-session calls before PostHog loads and PostHog's
 * own distinct id agree.
 *
 * Skipped entirely on the demo host and in the admin area; local dev only with ?qa=1 in the URL.
 */
import type { PostHog } from "posthog-js"

// Public client key (it ships in every page that loads PostHog).
const PH_TOKEN = "phc_w8jiUgnse2pJQ9WvcSQ3QEvZEhamGVzoAoXDdTnBYMoB"

const LMID_COOKIE = "_lmid"
const LANDING_KEY = "_lm_land"
const RECORDING_KEY = "_lm_rec"
const DEV_QA_KEY = "_lm_qa"
const ONCE_PREFIX = "_lm1:"
const MAX_QUEUE = 50
const ID_RE = /^[A-Za-z0-9_\-:.@$]{1,200}$/

/** Landing-URL params kept as session super properties (every utm_* is kept as well). */
const LANDING_PARAMS = new Set([
    "gclid", "gbraid", "wbraid", "msclkid", "fbclid", "ttclid", "rdt_cid",
    "campaignid", "adgroupid", "keyword", "matchtype", "creative", "ad", "network", "device", "targetid", "qa",
])

/** Never recorded: password fields, card fields (Evervault iframes / containers) and anything tagged ph-no-capture. */
const BLOCK_SELECTOR = [
    ".ph-no-capture",
    "input[type=\"password\"]",
    "input[autocomplete=\"current-password\"]",
    "input[autocomplete=\"new-password\"]",
    "input[autocomplete^=\"cc-\"]",
    "iframe[src*=\"evervault\"]",
    "[data-evervault]",
].join(", ")

const RECORDING_EVENTS = new Set(["checkout_started", "pay_pressed"])

let ph: PostHog | null = null
let state: "idle" | "waiting" | "loading" | "ready" | "off" = "idle"
const queue: Array<(p: PostHog) => void> = []
const onceMemory = new Set<string>()
let lastIdentify = ""

function readCookie(name: string): string | null {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
    return m ? decodeURIComponent(m[1]) : null
}

function writeCookie(name: string, value: string) {
    const secure = location.protocol === "https:" ? "; Secure" : ""
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=31536000; SameSite=Lax${secure}`
}

function newId(): string {
    try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID()
    } catch {
        /* fall through */
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
}

function excludedContext(): boolean {
    return location.hostname.split(".")[0] === "demo" || location.pathname.startsWith("/app/app_admin")
}

function devAllowed(): boolean {
    if (process.env.NODE_ENV === "production") return true
    try {
        if (new URLSearchParams(location.search).get("qa") === "1") sessionStorage.setItem(DEV_QA_KEY, "1")
        return sessionStorage.getItem(DEV_QA_KEY) === "1"
    } catch {
        return false
    }
}

/** First-party anonymous id (created on first use, kept a year). */
export function getLmid(): string | null {
    if (typeof document === "undefined") return null
    try {
        let id = readCookie(LMID_COOKIE)
        if (!id || !ID_RE.test(id)) {
            id = newId()
            writeCookie(LMID_COOKIE, id)
        }
        return id
    } catch {
        return null
    }
}

/** The id every setup-session call carries: PostHog's distinct id once loaded, else `_lmid`. */
export function getAnalyticsId(): string | null {
    try {
        const id = ph?.get_distinct_id()
        if (id && ID_RE.test(id)) return id
    } catch {
        /* fall through */
    }
    return getLmid()
}

/** Stores the landing URL and its ad/utm params once per browser-tab session. */
export function rememberLanding(href?: string): void {
    if (typeof window === "undefined") return
    try {
        if (sessionStorage.getItem(LANDING_KEY)) return
        const url = new URL(href || location.href)
        const props: Record<string, string> = { landing_url: url.href.slice(0, 1000) }
        url.searchParams.forEach((value, rawKey) => {
            const key = rawKey.toLowerCase()
            if (value && (key.startsWith("utm_") || LANDING_PARAMS.has(key))) props[key] = value.slice(0, 300)
        })
        sessionStorage.setItem(LANDING_KEY, JSON.stringify(props))
    } catch {
        /* storage unavailable: nothing to remember */
    }
}

/** Landing URL of this session (null when unknown). */
export function getLandingUrl(): string | null {
    if (typeof window === "undefined") return null
    try {
        rememberLanding()
        const raw = sessionStorage.getItem(LANDING_KEY)
        return raw ? (JSON.parse(raw).landing_url as string) || null : null
    } catch {
        return null
    }
}

/** True the first time `key` is seen in this tab session (sessionStorage, memory fallback). */
export function phOnce(key: string): boolean {
    const full = ONCE_PREFIX + key
    try {
        if (sessionStorage.getItem(full)) return false
        sessionStorage.setItem(full, "1")
        return true
    } catch {
        if (onceMemory.has(full)) return false
        onceMemory.add(full)
        return true
    }
}

function run(fn: (p: PostHog) => void) {
    if (state === "off" || typeof window === "undefined") return
    if (ph) {
        try {
            fn(ph)
        } catch {
            /* never throw into the caller */
        }
        return
    }
    if (queue.length < MAX_QUEUE) queue.push(fn)
}

function startRecording(p: PostHog) {
    try {
        sessionStorage.setItem(RECORDING_KEY, "1")
    } catch {
        /* ignore */
    }
    if (!p.sessionRecordingStarted()) p.startSessionRecording({ event_trigger: true })
}

export function phCapture(event: string, props?: Record<string, unknown>): void {
    run((p) => {
        if (RECORDING_EVENTS.has(event)) startRecording(p)
        p.capture(event, props)
    })
}

/** field_typed, once per field per session. Handle: the typed username; email: domain only; others: no value. */
export function phFieldTyped(field: "handle" | "email" | "name" | "other", raw: string | null | undefined): void {
    const value = (raw ?? "").trim()
    if (!value || !phOnce(`field:${field}`)) return
    const props: Record<string, unknown> = { field }
    if (field === "handle") props.value = value.replace(/^@+/, "").slice(0, 100)
    if (field === "email") {
        const at = value.lastIndexOf("@")
        if (at > 0 && at < value.length - 1) props.domain = value.slice(at + 1).toLowerCase().slice(0, 100)
    }
    phCapture("field_typed", props)
}

export function phIdentify(userId: string | null | undefined, traits: { email?: string | null; name?: string | null; handle?: string | null }): void {
    if (!userId) return
    const clean: Record<string, string> = {}
    for (const [k, v] of Object.entries(traits)) if (typeof v === "string" && v.trim()) clean[k] = v.trim()
    const key = `${userId}|${JSON.stringify(clean)}`
    if (key === lastIdentify) return
    lastIdentify = key
    run((p) => p.identify(userId, clean))
}

function loadPosthog() {
    if (state !== "waiting") return
    state = "loading"
    const lmid = getLmid()
    import("posthog-js")
        .then(({ default: posthog }) => {
            let resumeRecording = false
            try {
                resumeRecording = sessionStorage.getItem(RECORDING_KEY) === "1"
            } catch {
                /* ignore */
            }
            posthog.init(PH_TOKEN, {
                api_host: "/_ev",
                ui_host: "https://us.posthog.com",
                persistence: "localStorage",
                person_profiles: "identified_only",
                capture_pageview: "history_change",
                capture_pageleave: false,
                autocapture: false,
                capture_heatmaps: false,
                capture_dead_clicks: false,
                capture_exceptions: false,
                capture_performance: { web_vitals: false },
                disable_surveys: true,
                disable_product_tours: true,
                disable_web_experiments: true,
                disable_conversations: true,
                advanced_disable_feature_flags_on_first_load: true,
                // Replay starts only on checkout_started / pay_pressed (startRecording), and resumes after a reload in that tab.
                disable_session_recording: !resumeRecording,
                bootstrap: lmid ? { distinctID: lmid } : undefined,
                before_send: (event) => (excludedContext() ? null : event),
                session_recording: {
                    maskAllInputs: false,
                    maskInputOptions: { password: true },
                    blockSelector: BLOCK_SELECTOR,
                },
            })
            ph = posthog
            state = "ready"
            try {
                // Keep `_lmid` equal to PostHog's anonymous id if PostHog already had one from an earlier visit.
                const id = posthog.get_distinct_id()
                if (posthog.get_property("$user_state") !== "identified" && id && ID_RE.test(id) && id !== lmid) writeCookie(LMID_COOKIE, id)
                const raw = sessionStorage.getItem(LANDING_KEY)
                if (raw) posthog.register_for_session(JSON.parse(raw))
            } catch {
                /* ignore */
            }
            const pending = queue.splice(0)
            for (const fn of pending) run(fn)
        })
        .catch(() => {
            state = "off"
            queue.length = 0
        })
}

/**
 * Called by PostHogLoader after window load + idle time (the loader imports this module only then,
 * so neither this module nor posthog-js is in any page's initial bundle). `landingHref` is the URL
 * the page opened with, read by the loader at mount.
 */
export function startPosthog(landingHref?: string): void {
    if (typeof window === "undefined" || state !== "idle") return
    try {
        if (landingHref && new URL(landingHref).searchParams.get("qa") === "1") sessionStorage.setItem(DEV_QA_KEY, "1")
    } catch {
        /* ignore */
    }
    try {
        if (excludedContext() || !devAllowed()) {
            state = "off"
            queue.length = 0
            return
        }
        state = "waiting"
        rememberLanding(landingHref)
        loadPosthog()
    } catch {
        state = "off"
    }
}
